import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { I18nString, PermitCategory } from '../../../models/permit-category.model';

@Component({
    selector: 'app-permit-category-step',
    templateUrl: './permit-category-step.component.html',
    styleUrls: ['./permit-category-step.component.scss'],
})
export class PermitCategoryStepComponent implements OnInit, OnChanges, OnDestroy {
    @Input() categories: PermitCategory[] = [];

    @Input() loading = false;

    @Input() selectedCategory: PermitCategory | null = null;

    @Output() categorySelected = new EventEmitter<PermitCategory>();

    searchQuery = '';

    filteredCategories: PermitCategory[] = [];

    private searchSubject = new Subject<string>();

    private searchSubscription: Subscription;

    constructor(private translate: TranslateService) {}

    ngOnInit(): void {
        this.searchSubscription = this.searchSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe((query) => this.filterCategories(query));
        this.filterCategories(this.searchQuery);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.categories) {
            this.filterCategories(this.searchQuery);
        }
    }

    ngOnDestroy(): void {
        this.searchSubscription?.unsubscribe();
    }

    /** Category names are localized objects ({ fr, en }); resolve to the current language. */
    localizedName(name: I18nString | string): string {
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang || 'fr';
        return name[lang] || name.fr || name.en || '';
    }

    onSearchChange(): void {
        this.searchSubject.next(this.searchQuery);
    }

    isSelected(category: PermitCategory): boolean {
        return this.selectedCategory?._id === category._id;
    }

    typeCount(category: PermitCategory): number {
        return (category.permitTypes || []).filter((t) => t.active !== false).length;
    }

    selectCategory(category: PermitCategory): void {
        this.categorySelected.emit(category);
    }

    private filterCategories(query: string): void {
        const term = (query || '').toLowerCase().trim();
        if (!term) {
            this.filteredCategories = [...(this.categories || [])];
            return;
        }
        this.filteredCategories = (this.categories || []).filter((category) => this.localizedName(category.name).toLowerCase().includes(term));
    }
}
